import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../../shared/ui/Modal';
import { useAuth } from '../../../../features/system/auth/hooks/useAuth';
import { addProjectPermission, type PermissionLevel } from '../api/userProjectPermissionsApi';
import {
  getProjectMembers,
  revokeProjectMember,
  type ProjectMember,
} from '../api/projectsApi';

// Basic email format validation (covers common valid patterns)
const VALID_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value: string): boolean {
  return VALID_EMAIL_REGEX.test(value.trim());
}

function formatPermissionLabel(permission: ProjectMember['permission']): string {
  if (permission === 'owner') return 'Owner';
  if (permission === 'readwrite') return 'Read & write';
  return 'Read';
}

/** Primary line: name or email; second line: email when name is shown; raw id only if neither resolved. */
function MemberIdentity({ m }: { m: ProjectMember }) {
  const name = m.name?.trim();
  const email = m.email?.trim();
  const primary = name || email || null;
  const secondary = name && email && name !== email ? email : null;
  const hasHumanLabel = !!(name || email);

  return (
    <div className="min-w-0">
      <div className="text-gray-200 truncate" title={primary ?? m.user_id}>
        {primary ?? 'Unknown user'}
      </div>
      {secondary && (
        <div className="text-gray-500 text-xs truncate" title={secondary}>
          {secondary}
        </div>
      )}
      {!hasHumanLabel && (
        <div className="text-gray-500 text-xs font-mono truncate" title={m.user_id}>
          {m.user_id}
        </div>
      )}
    </div>
  );
}

export interface ShareProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string | number;
  projectName?: string;
}

const ShareProjectModal: React.FC<ShareProjectModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
}) => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<PermissionLevel>('read');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [revokingUserId, setRevokingUserId] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    const pid = String(projectId).trim();
    if (!pid) return;
    setMembersLoading(true);
    setMembersError(null);
    try {
      const { members: list } = await getProjectMembers(pid);
      setMembers(list);
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : 'Failed to load people with access.');
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!isOpen) return;
    void loadMembers();
  }, [isOpen, loadMembers]);

  const reset = () => {
    setEmail('');
    setPermission('read');
    setError(null);
    setSuccess(false);
    setMembers([]);
    setMembersError(null);
    setRevokingUserId(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleRevoke = async (memberUserId: string) => {
    setMembersError(null);
    setRevokingUserId(memberUserId);
    try {
      await revokeProjectMember(projectId, memberUserId);
      await loadMembers();
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : 'Failed to remove access.');
    } finally {
      setRevokingUserId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter an email address.');
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    const currentUserEmail = user?.email?.trim().toLowerCase();
    if (currentUserEmail && trimmed.toLowerCase() === currentUserEmail) {
      setError('You cannot share this project with yourself. Enter another user\'s email.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await addProjectPermission({
        user_email: trimmed,
        project_id: String(projectId),
        permission,
      });
      setEmail('');
      setPermission('read');
      await loadMembers();
      setSuccess(true);
      setTimeout(handleClose, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share project.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Share project" size="md">
      <form onSubmit={handleSubmit} className="space-y-5">
        <p className="text-gray-300">
          Would you like to share this project with another user?
          {projectName && (
            <span className="block mt-1 text-sm text-gray-400">
              Project: {projectName}
            </span>
          )}
        </p>

        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-2">People with access</h3>
          {membersLoading && (
            <p className="text-sm text-gray-400">Loading…</p>
          )}
          {!membersLoading && members.length === 0 && !membersError && (
            <p className="text-sm text-gray-400">No one else has access yet.</p>
          )}
          {membersError && (
            <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm mb-2">
              {membersError}
            </div>
          )}
          {!membersLoading && members.length > 0 && (
            <ul className="border border-white/10 rounded-lg divide-y divide-white/10 max-h-48 overflow-y-auto">
              {members.map((m) => (
                <li
                  key={m.user_id}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
                >
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <MemberIdentity m={m} />
                    <div className="text-gray-500 text-xs">{formatPermissionLabel(m.permission)}</div>
                  </div>
                  {m.permission !== 'owner' && (
                    <button
                      type="button"
                      onClick={() => void handleRevoke(m.user_id)}
                      disabled={revokingUserId !== null || submitting || success}
                      className="shrink-0 px-2 py-1 rounded text-red-300 hover:bg-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                    >
                      {revokingUserId === m.user_id ? 'Removing…' : 'Remove access'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label htmlFor="share-email" className="block text-sm font-medium text-gray-300 mb-1">
            Email address
          </label>
          <input
            id="share-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@example.com"
            className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={submitting || success}
            autoComplete="email"
          />
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-300 mb-2">Permission level</span>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="permission"
                value="read"
                checked={permission === 'read'}
                onChange={() => setPermission('read')}
                disabled={submitting || success}
                className="w-4 h-4 text-blue-500 bg-white/10 border-white/20 focus:ring-blue-500"
              />
              <span className="text-gray-300">Read</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="permission"
                value="readwrite"
                checked={permission === 'readwrite'}
                onChange={() => setPermission('readwrite')}
                disabled={submitting || success}
                className="w-4 h-4 text-blue-500 bg-white/10 border-white/20 focus:ring-blue-500"
              />
              <span className="text-gray-300">Read &amp; Write</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 rounded-lg bg-green-500/20 border border-green-500/30 text-green-200 text-sm">
            Project shared successfully.
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || success}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-colors"
          >
            {submitting ? 'Sharing…' : success ? 'Shared' : 'Share'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ShareProjectModal;
